// board-draw: how a leaderboard looks, shared by the site's board chunk (board.js) and the booking
// page's preview (preview.js), so a bidder sees the board exactly as visitors will.
// Names and text are set as text, never HTML, and only https links are made (plain http only for localhost).

import { https } from './dom.js';

/** @typedef {import('./config.js').Board} Board */

// The site's font comes in through the slot (render.js); the board keeps the units' 16px padding.
export const BOARD_CSS = `.sl-b{box-sizing:border-box;width:100%;max-width:640px;margin:0 auto;padding:16px;font-size:14px;line-height:1.4;color:var(--sl-fg,inherit);background:var(--sl-card);border:1px solid var(--sl-line);border-radius:var(--sl-r)}
.sl-bl{list-style:none;margin:0 0 12px;padding:0}
.sl-br{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--sl-line)}
.sl-br:first-child{padding-top:0}
.sl-br:last-child{border-bottom:0;padding-bottom:0}
.sl-bn{flex:none;width:2.2em;font-weight:700;opacity:.7}
.sl-bg{flex:none;display:grid;place-items:center;width:28px;height:28px;overflow:hidden;border-radius:6px;background:var(--sl-soft);color:#fff;font-weight:700}
.sl-bg img{width:100%;height:100%;object-fit:contain;background:#fff}
.sl-bw{display:flex;flex:1;flex-direction:column;min-width:0}
.sl-bt,.sl-bw small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.sl-bt{font-weight:600;color:inherit;text-decoration:none}
a.sl-bt:hover{text-decoration:underline}
.sl-bw small{opacity:.7}
.sl-ba{flex:none;font-weight:700}
.sl-bs{flex:none;font-size:12px;opacity:.7}
.sl-bf{display:flex;flex-wrap:wrap;align-items:center;gap:12px}
.sl-b0 .sl-bf{justify-content:center}
.sl-bb{display:inline-block;padding:8px 14px;border-radius:calc(var(--sl-r) * .75);background:var(--sl-btn);color:var(--sl-btnfg);font-weight:600;text-decoration:none}
.sl-bv{font-size:12px;color:inherit;opacity:.6}`;

/** @param {string} tag @param {string} [cls] @param {string} [text] */
function el(tag, cls, text) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text) e.textContent = text;
    return e;
}

/** @param {string} cls @param {string} text @param {string} href */
function link(cls, text, href) {
    return Object.assign(el('a', cls, text), { href, target: '_blank', rel: 'noopener' });
}

/** @param {HTMLElement} box @param {Board} b */
export function drawBoard(box, b) {
    const root = el('div', 'sl-b'), list = el('ol', 'sl-bl');
    b.rows.forEach((r, i) => {
        const row = el('li', 'sl-br'), logo = el('span', 'sl-bg'), who = el('span', 'sl-bw');
        const src = https(r.logo);
        if (src) logo.append(Object.assign(el('img'), { src, alt: '', loading: 'lazy' }));
        else {
            logo.textContent = r.name.slice(0, 1).toUpperCase();
            if (/^#[0-9a-f]{6}$/i.test(r.color || '')) logo.style.background = /** @type {string} */ (r.color);
        }
        // the name links to the sponsor; s.js counts the click (sl-c) for this entry (data-e)
        const href = https(r.href);
        const name = href ? Object.assign(link('sl-c sl-bt', r.name, href), { rel: 'sponsored noopener' }) : el('span', 'sl-bt', r.name);
        if (href) name.dataset.e = String(r.e);
        who.append(name);
        if (r.text) who.append(el('small', '', r.text));
        row.append(el('span', 'sl-bn', `#${i + 1}`), logo, who);
        if (r.clicks !== undefined) row.append(el('span', 'sl-bs', `${r.clicks} clicks`));
        if (r.amount) row.append(el('span', 'sl-ba', r.amount));
        list.append(row);
    });
    if (b.rows.length) root.append(list);
    else root.classList.add('sl-b0');   // empty: its "Be #1" in the middle, like an open spot's line
    const foot = el('div', 'sl-bf');
    const cta = b.cta && https(b.cta.href);
    if (b.cta && cta) foot.append(link('sl-bb', b.cta.text, cta));
    if (b.views !== undefined) foot.append(el('span', 'sl-bs', `${b.views} views`));
    const via = https(b.via);
    if (via) foot.append(link('sl-bv', 'via Sponsor.st', via));
    root.append(foot);
    box.append(root);
}
